import * as React from 'react';
import { Index, Configure } from 'react-instantsearch-core';
import { MediaQuery } from '@grandvisionhq/common';
import { Container, Heading } from '@grandvisionhq/elements';
import { useIntl } from '@grandvisionhq/state';
import FacetsMobile from '../Algolia/components/FacetsMobile/FacetsMobile.js';
import FacetsDesktop from '../Algolia/components/FacetsDesktop/FacetsDesktop.js';
import '../Algolia/components/VirtualFacets/VirtualFacets.js';
import '../Algolia/components/SortBy/SortBy.js';
import { AlgoliaSearch } from '../Algolia/AlgoliaSearch.js';
import { useAlgoliaConfig } from '../Algolia/AlgoliaConfig.js';
import '@swc/helpers';
import 'algoliasearch/lite';
import 'lodash/merge';
import { isFacetVisible, PrimaryFacets } from '../types.js';
import '@grandvisionhq/utils';
import '../Algolia/utils/query-string.js';
import { useAlgoliaData } from '../Algolia/AlgoliaDataProvider.js';
import '../Algolia/AlgoliaAnalytics.js';
import '@grandvisionhq/products';
import '../CrossSellProducts/utils/create-algolia-filters.js';
import hasProductTypeFacet from '../Algolia/utils/has-Product-type-facet.js';
import { mapSearchIndicesToRecord } from '../Algolia/utils/map-search-indices-to-record.js';
import { ConnectedProductListing } from '../ConnectedProductListing/ConnectedProductListing.js';
import NoSearchResults from '../NoSearchResults/NoSearchResults.js';
import { ConnectedContentListing } from '../SearchResultsContent/ConnectedResultsContent.js';
import SearchOrderBy from './components/SearchOrderBy.js';
import SearchTabs from './components/SearchTabs.js';
import { getHitCounts } from './utils/get-hit-counts.js';

const SearchIndexByTabIndex = {
    0: 'productsIndex',
    1: 'contentIndex'
};
const hashNames = [
    'products',
    'pages'
];
const SearchPage = ({ pageTitle , algoliaConfig , reimbursableLogos  })=>{
    return /*#__PURE__*/ React.createElement("div", {
        className: "search-page"
    }, /*#__PURE__*/ React.createElement(AlgoliaSearch, null, /*#__PURE__*/ React.createElement(SearchPageContent, {
        pageTitle: pageTitle,
        algoliaConfig: algoliaConfig,
        reimbursableLogos: reimbursableLogos
    })));
};
const SearchPageContent = ({ pageTitle , algoliaConfig , reimbursableLogos  })=>{
    var ref;
    const { getLabel  } = useIntl();
    const { facets , sortIndices , algoliaInstantSearchProps , pageSize  } = useAlgoliaConfig();
    const isMobile = MediaQuery.useBreakpoint() === MediaQuery.BreakpointName.PHONE;
    const hasProductType = hasProductTypeFacet(algoliaInstantSearchProps.searchState, facets);
    const facetsVisible = facets.filter((facet)=>isFacetVisible(facet) && (hasProductType || PrimaryFacets[facet.id])
    );
    const [activeTabIndex, setActiveTabIndex] = React.useState(0);
    const handleTabClick = React.useCallback((index)=>{
        setActiveTabIndex(index);
        history.replaceState(null, '', `#${hashNames[index]}`);
    }, []);
    React.useEffect(()=>{
        const hash = window.location.hash.replace(/^#/, '');
        const hashIndex = hashNames.indexOf(hash);
        if (hashIndex > -1) {
            setActiveTabIndex(hashIndex);
        }
    }, []);
    /**
   * This flag is used to check if we should compare the nbHits
   * and change the tab accordingly
   */ const hasInitialTabCheck = React.useRef(false);
    React.useEffect(()=>{
        hasInitialTabCheck.current = true;
    }, []);
    const indexes = mapSearchIndicesToRecord(algoliaConfig.indexes || []);
    const productIndex = algoliaConfig.productIndex;
    indexes.productsIndex = productIndex;
    const contentIndex = indexes.contentIndex;
    const activeIndex = indexes[SearchIndexByTabIndex[activeTabIndex]];
    const amountOfHitsLabel = activeTabIndex === 0 ? getLabel('search.results.amount.title') : getLabel('search.results.content.amount.title');
    function changeTabByCount(productsHitCount, contentHitCount) {
        if (productsHitCount === 0 && contentHitCount > 0 && !hasInitialTabCheck.current) {
            setActiveTabIndex(1);
        }
    }
    const { allSearchResults , searchQuery  } = useAlgoliaData();
    const { productsHitCount: productsHitCount1 , contentHitCount: contentHitCount1 , totalHitCount  } = getHitCounts(allSearchResults, indexes);
    changeTabByCount(productsHitCount1, contentHitCount1);
    return totalHitCount ? /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement("div", {
        className: "search-page-header"
    }, /*#__PURE__*/ React.createElement(Container, {
        className: "search-page-header__container"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "search-page-header__intro"
    }, pageTitle && /*#__PURE__*/ React.createElement(Heading, {
        variant: "medium",
        component: "h1",
        className: "search-page-header__title",
        "data-t": "search-page-heading"
    }, pageTitle)), /*#__PURE__*/ React.createElement("div", {
        className: "search-page-header__tabs"
    }, /*#__PURE__*/ React.createElement(SearchTabs, {
        activeTabIndex: activeTabIndex,
        setActiveTabIndex: handleTabClick,
        indexes: indexes,
        productIndex: productIndex
    }), isMobile && activeTabIndex === 0 && /*#__PURE__*/ React.createElement(FacetsMobile, {
        facets: facetsVisible
    }), /*#__PURE__*/ React.createElement(SearchOrderBy, {
        sortIndices: sortIndices,
        showSortBy: activeTabIndex === 0,
        activeIndex: activeIndex,
        amountOfHitsLabel: amountOfHitsLabel
    }))), !isMobile && activeTabIndex === 0 && /*#__PURE__*/ React.createElement(FacetsDesktop, {
        facets: facetsVisible
    })), /*#__PURE__*/ React.createElement("div", {
        className: "search-page-content"
    }, activeTabIndex === 0 && /*#__PURE__*/ React.createElement(ConnectedProductListing, {
        reimbursableLogos: reimbursableLogos
    }), contentIndex && /*#__PURE__*/ React.createElement(Index, {
        indexName: contentIndex.name
    }, /*#__PURE__*/ React.createElement(Configure, {
        offset: 0,
        length: isMobile ? 8 : pageSize
    }), activeTabIndex === 1 && /*#__PURE__*/ React.createElement(ConnectedContentListing, null)))) : /*#__PURE__*/ React.createElement(NoSearchResults, {
        indexName: (ref = indexes.suggestionsIndex) === null || ref === void 0 ? void 0 : ref.name,
        query: searchQuery
    });
};

export { SearchPage as default };
//# sourceMappingURL=SearchPage.js.map
